// game/interpolate.players.ts — Player interpolation with client-side prediction

import { smoothPosition, smoothRotation, wrapCoordinate } from "./smoothing";

let predictOwnPlayer: () => void;

export function setPredictOwnPlayerFunction(fn: () => void) {
  predictOwnPlayer = fn;
}

let state: any;

export function setPlayerInterpolationState(gameState: any) {
  state = gameState;
}

export function interpolatePlayers() {
  const now = performance.now();
  const timeSinceUpdate = now - state.lastStateAt;
  const predictTime = timeSinceUpdate / 1000;
  const { w, h } = state.world;

  for (const [id, target] of state.playersTarget) {
    const current = state.players.get(id);
    if (current && target) {
      // For own player, use client-side prediction with server reconciliation
      if (id === state.playerId) {
        if (predictOwnPlayer) {
          predictOwnPlayer();
        }

        if (state.predictedPlayer) {
          if (!state.visualPlayer) {
            state.visualPlayer = {
              pos: {
                x: state.predictedPlayer.pos.x,
                y: state.predictedPlayer.pos.y,
              },
              vel: {
                x: state.predictedPlayer.vel.x,
                y: state.predictedPlayer.vel.y,
              },
              rotation: state.predictedPlayer.rotation,
            };
          }

          smoothPosition({
            current: state.visualPlayer.pos,
            target: state.predictedPlayer.pos,
            world: state.world,
          });

          const vxDiff = state.predictedPlayer.vel.x - state.visualPlayer.vel.x;
          const vyDiff = state.predictedPlayer.vel.y - state.visualPlayer.vel.y;

          let dx = state.predictedPlayer.pos.x - state.visualPlayer.pos.x;
          let dy = state.predictedPlayer.pos.y - state.visualPlayer.pos.y;
          if (dx > w / 2) dx -= w;
          if (dx < -w / 2) dx += w;
          if (dy > h / 2) dy -= h;
          if (dy < -h / 2) dy += h;
          const distance = Math.sqrt(dx * dx + dy * dy);
          let smoothingFactor = 0.15;
          if (distance > 100) smoothingFactor = 0.3;
          else if (distance > 50) smoothingFactor = 0.2;

          state.visualPlayer.vel.x += vxDiff * smoothingFactor;
          state.visualPlayer.vel.y += vyDiff * smoothingFactor;

          state.visualPlayer.rotation = smoothRotation(
            state.visualPlayer.rotation,
            state.predictedPlayer.rotation,
            smoothingFactor * 1.5,
          );

          current.pos = state.visualPlayer.pos;
          current.vel = state.visualPlayer.vel;
          current.rotation = state.visualPlayer.rotation;
          current.targetRotation = state.predictedPlayer.targetRotation;
        } else {
          let predictedX = target.pos.x + target.vel.x * predictTime;
          let predictedY = target.pos.y + target.vel.y * predictTime;

          predictedX = wrapCoordinate(predictedX, w);
          predictedY = wrapCoordinate(predictedY, h);

          current.pos.x = predictedX;
          current.pos.y = predictedY;
          current.rotation = target.rotation;
          current.targetRotation = target.targetRotation;

          state.visualPlayer = {
            pos: { x: current.pos.x, y: current.pos.y },
            vel: { x: target.vel.x, y: target.vel.y },
            rotation: current.rotation,
          };
        }

        current.health = target.health;
        current.maxHealth = target.maxHealth;
        current.kills = target.kills;
        current.deaths = target.deaths;
        current.name = target.name;
        current.pelletsCollected = target.pelletsCollected;
        current.scale = target.scale;
        current.damageMultiplier = target.damageMultiplier;
        current.speedMultiplier = target.speedMultiplier;
        continue;
      }

      let predictedX = target.pos.x + target.vel.x * predictTime;
      let predictedY = target.pos.y + target.vel.y * predictTime;

      predictedX = wrapCoordinate(predictedX, w);
      predictedY = wrapCoordinate(predictedY, h);

      const predictedRotation =
        target.rotation + target.angularVelocity * predictTime;

      smoothPosition({
        current: current.pos,
        target: { x: predictedX, y: predictedY },
        world: state.world,
      });

      current.rotation = smoothRotation(
        current.rotation,
        predictedRotation,
        0.225,
      );
      current.targetRotation = target.targetRotation;

      current.health = target.health;
      current.maxHealth = target.maxHealth;
      current.kills = target.kills;
      current.deaths = target.deaths;
      current.name = target.name;
      current.pelletsCollected = target.pelletsCollected;
      current.scale = target.scale;
      current.damageMultiplier = target.damageMultiplier;
      current.speedMultiplier = target.speedMultiplier;
    }
  }
}
